import {
  START_LOCATION,
  type NavigationGuardReturn,
  type RouteLocationNormalized,
} from 'vue-router';
import {
  buildHomologationListRoute,
  isNotFoundError,
  isRecordDetailRoute,
  type HomologationDetailBasePath,
} from '@/shared/lib';

import { primeHomologationItem, type HomologationItemStage } from '../api/homologationItemCache';
import {
  warnHomologationItemNotPending,
  type HomologationItemMessages,
} from './homologationItemFeedback';

export interface HomologationItemGuardOptions {
  stage: HomologationItemStage;
  listPath: HomologationDetailBasePath;
  messages: HomologationItemMessages;
}

export type HomologationItemGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) => Promise<NavigationGuardReturn>;

/**
 * Builds the guard that checks a homologation detail item is still pending before its page renders,
 * priming the request so the page reuses it instead of fetching the item again.
 */
export function createHomologationItemGuard(
  options: HomologationItemGuardOptions
): HomologationItemGuard {
  return async (to, from) => {
    if (to.path === from.path) {
      return true;
    }

    const isRecord = isRecordDetailRoute(to.params.nature);

    try {
      await primeHomologationItem(options.stage, String(to.params.id), isRecord);
      return true;
    } catch (error) {
      // Only a missing item is known to be decided; any other failure is left to the page.
      if (!isNotFoundError(error)) {
        return true;
      }
    }

    warnHomologationItemNotPending(isRecord, options.messages);

    // Aborting the very first navigation would leave a blank screen, since there is no page to stay on.
    return from === START_LOCATION
      ? buildHomologationListRoute(options.listPath, isRecord)
      : false;
  };
}
